import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  Loader2, ArrowRight, Building2, MapPin, Briefcase, FileText, Globe, Shield
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfessionalSetupPage() {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    firm_name: "",
    specialization: "",
    location: "",
    bio: "",
    is_public: true,
  });

  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/ca/profile`, form, { withCredentials: true });
      toast.success("Profile set up! Welcome to InvoiceSetu.");
      navigate("/ca/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    navigate("/ca/dashboard");
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#7C3AED]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4 py-12" data-testid="professional-setup-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-violet-100 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-7 h-7 text-violet-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Set up your Professional Profile
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            This helps clients find and recognize you. You can always update this later in Settings.
          </p>
          {user?.professional_id && (
            <p className="text-xs font-mono font-semibold text-violet-600 bg-violet-50 px-3 py-1 rounded-lg border border-violet-100 inline-block mt-3" data-testid="setup-pro-id">
              Your ID: {user.professional_id}
            </p>
          )}
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_20px_60px_rgb(0,0,0,0.06)] p-6 sm:p-8">
          <div className="space-y-5">
            {/* Firm Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Firm / Practice Name</label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={form.firm_name}
                  onChange={(e) => update("firm_name", e.target.value)}
                  placeholder="e.g. Kumar & Associates"
                  className="pl-10"
                  data-testid="setup-firm-name"
                />
              </div>
            </div>

            {/* Specialization */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Specialization</label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={form.specialization}
                  onChange={(e) => update("specialization", e.target.value)}
                  placeholder="e.g. GST Compliance, Tax Advisory, Audit"
                  className="pl-10"
                  data-testid="setup-specialization"
                />
              </div>
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Location</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={form.location}
                  onChange={(e) => update("location", e.target.value)}
                  placeholder="e.g. Mumbai, Maharashtra"
                  className="pl-10"
                  data-testid="setup-location"
                />
              </div>
            </div>

            {/* Bio */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">About You</label>
              <Textarea
                value={form.bio}
                onChange={(e) => update("bio", e.target.value)}
                placeholder="Brief description of your practice, experience, and services..."
                rows={3}
                className="text-sm"
                data-testid="setup-bio"
              />
            </div>

            {/* Public Profile Toggle */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
              <div className="flex items-center gap-2.5">
                <Globe className="w-4 h-4 text-violet-500" />
                <div>
                  <p className="text-sm font-medium text-slate-700">Public Profile</p>
                  <p className="text-xs text-slate-500">Allow clients to find you via your Professional ID</p>
                </div>
              </div>
              <button
                onClick={() => update("is_public", !form.is_public)}
                className={`relative w-11 h-6 rounded-full transition-colors ${form.is_public ? "bg-violet-600" : "bg-slate-300"}`}
                data-testid="setup-public-toggle"
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${form.is_public ? "translate-x-5" : ""}`} />
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between mt-8 pt-5 border-t border-slate-100">
            <button
              onClick={handleSkip}
              className="text-sm text-slate-500 hover:text-slate-700 font-medium"
              data-testid="setup-skip-btn"
            >
              Skip for now
            </button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-violet-600 hover:bg-violet-700 text-white px-6"
              data-testid="setup-complete-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <ArrowRight className="w-4 h-4 mr-2" />
              )}
              Complete Setup
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
